import React, { useEffect, useMemo, useState } from "react";
import {
    Card, Row, Col, Select, DatePicker, Button, Table, Tag, Typography,
    Space, Progress, Tooltip, Popconfirm, Empty, message, Tabs,
} from "antd";
import {
    TeamOutlined, AimOutlined, CheckCircleOutlined, ClockCircleOutlined,
    WarningOutlined, PlusOutlined, HistoryOutlined, FilterOutlined,
    EyeOutlined, EditOutlined, DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

import { useNavigate } from "react-router-dom";
import { SalesTargetService } from "../../Services/Manage/SalesTargetService";
import { ConfigurationService } from "../../Services/Manage/ConfigurationService";
import { NotificationService } from "../../Services/Manage/NotificationService";
import { EnquiryService } from "../../Services/Manage/EnquiryService";


dayjs.extend(quarterOfYear);

const PRIMARY = "rgba(11, 93, 148, 0.95)";
const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const StatCard = ({ icon, label, value, sub, subColor, color }) => (
    <Card size="small" styles={{ body: { padding: 14 } }} style={{ height: "100%", borderRadius: 12 }}>
        <Space align="start" size={12}>
            <div style={{
                width: 42, height: 42, borderRadius: 11,
                background: `${color}1a`, color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0,
            }}>
                {icon}
            </div>
            <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{label}</Typography.Text>
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25 }}>{value}</div>
                {sub && <Typography.Text style={{ fontSize: 11, color: subColor || "#999" }}>{sub}</Typography.Text>}
            </div>
        </Space>
    </Card>
);

const statusFor = (achPct) => {
    if (achPct >= 70) return { label: "On Track", color: "green" };
    if (achPct >= 40) return { label: "At Risk", color: "orange" };
    return { label: "Behind", color: "red" };
};

const SalesTargets = () => {
    const perm = getPermission("/sales-target");
    const navigate = useNavigate();
    const [msg, msgCtx] = message.useMessage();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [territories, setTerritories] = useState([]);
    const [totalEmployees, setTotalEmployees] = useState(0);

    const [enquiries, setEnquiries] = useState([]);
    const [wonStatuses, setWonStatuses] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [invoices, setInvoices] = useState([]);

    const [targetFor, setTargetFor] = useState("Sales Executive"); // "Sales Executive" | "Sales Team"
    const [period, setPeriod] = useState("Monthly");
    const [month, setMonth] = useState(dayjs());
    const [territory, setTerritory] = useState(null);
    const [status, setStatus] = useState(null);

    const loadTargets = async () => {
        setLoading(true);
        try {
            const res = await SalesTargetService.getAll();
            setRows(res.data || []);
        } catch (e) {
            console.error(e);
            msg.error("Failed to load targets");
        } finally {
            setLoading(false);
        }
    };

    const loadConfigs = async () => {
        try {
            const terr = await ConfigurationService.getByType("territory");
            setTerritories((terr.data || []).map((i) => ({ value: i.label, label: i.label })));
        } catch (e) { console.error(e); }
    };

    const loadEnquiries = async () => {
        try {
            const res = await EnquiryService.getAllEnquiries();
            setEnquiries(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadQuotations = async () => {
        try {
            const { QuotationService } = await import("../../Services/Manage/QuotationService");
            const res = await QuotationService.getAllQuotation();
            setQuotations(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadInvoices = async () => {
        try {
            const { InvoiceService } = await import("../../Services/Manage/InvoiceService");
            const res = await InvoiceService.getAllInvoice();
            setInvoices(res.data || []);
        } catch (e) { console.error(e); }
    };

    const collectedOf = (inv) => (inv.payments || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);

    const loadUserMap = async () => {
        try {
            const { UserService } = await import("../../Services/Manage/UserService");
            const res = await UserService.getAllUsers();
            const list = res?.data || res || [];
            const map = {};
            list.forEach((u) => {
                const uid = u.uid || u.user_id || u.id;
                const nm = u.name || u.user_name || u.username;
                if (uid && nm) map[String(uid).trim()] = nm;
            });
            setUserMap(map);
        } catch (e) { console.error(e); }
    };
    const loadWonStatuses = async () => {
        try {
            const res = await ConfigurationService.getByType("enquiry_status");
            const all = res.data || [];
            setWonStatuses(all.filter(i => i.label.trim().toLowerCase() === "won").map(i => i.label.trim()));
        } catch (e) { console.error(e); }
    };

    const parseItems = (items) => {
        if (Array.isArray(items)) return items;
        try { return JSON.parse(items || "[]"); } catch { return []; }
    };
    const amountOf = (d) => {
        let amt = parseFloat(String(d.total_amount ?? 0).replace(/[^0-9.-]/g, "")) || 0;
        if (!amt) {
            const s = parseItems(d.service_items);
            const p = parseItems(d.product_items);
            amt = [...s, ...p].reduce((a, it) => a + (Number(it.unit_price) || 0), 0);
        }
        return amt;
    };

    const loadEmployeeCount = async () => {
        try {
            const { AddEmployeesService } = await import("../../Services/Manage/AddEmployeesService");
            const res = await AddEmployeesService.getByDepartment("Sales");
            const emps = res.data || [];
            setTotalEmployees(emps.filter(e => e.status === "Active").length);
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        loadTargets();
        loadConfigs();
        loadEmployeeCount();
        loadEnquiries();
        loadWonStatuses();
        loadQuotations();
        loadInvoices();
        loadUserMap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleDelete = async (id) => {
        try {
            const res = await SalesTargetService.remove(id);
            if (res.status) {
                try { await NotificationService.removeByTarget(id); } catch { }
                msg.success("Target deleted");
                loadTargets();
            } else {
                msg.error(res.message || "Failed to delete");
            }
        } catch (e) {
            console.error(e);
            msg.error("Failed to delete");
        }
    };

    // Build a per-quotation list with resolved name(s), conversion date, and the
    // amount actually COLLECTED (sum of linked invoice payments) — not the
    // quoted/converted face value — so "Achieved" matches real money received,
    // consistent with Achieved Revenue elsewhere in the app.
    const convertedQuotations = useMemo(() => {
        return quotations
            .filter((q) => (q.quotation_status || "").toLowerCase() === "converted")
            .map((q) => {
                const linkedInvoices = invoices.filter((inv) =>
                    inv.qu_no === q.qu_no || inv.source_qu_no === q.qu_no || inv.reference === q.qu_no
                );
                let names = (q.assigned_to || "").split(",").map(s => s.trim()).filter(Boolean);
                if (names.length === 0 && q.added_by) {
                    const resolvedName = userMap[String(q.added_by).trim()];
                    if (resolvedName) names = [resolvedName];
                }
                const amt = linkedInvoices.length > 0
                    ? linkedInvoices.reduce((s, inv) => s + collectedOf(inv), 0)
                    : invoices
                        .filter((inv) => {
                            const invNames = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                            return names.some(n => invNames.includes(n)) ||
                                (invNames.length === 0 && names.includes(userMap[String(inv.added_by || "").trim()]));
                        })
                        .filter((inv) => inv.qu_no === q.qu_no)
                        .reduce((s, inv) => s + collectedOf(inv), 0);
                const dateStr = q.updated_on || q.date || q.added_on || null;
                return { names, amt, date: dateStr ? dayjs(dateStr) : null };
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotations, invoices, userMap]);

    // Sum only the quotations whose conversion date falls inside [start_date, end_date] for this specific target row
    const achievedForNameInRange = (name, startDate, endDate) => {
        if (!name) return 0;
        const start = startDate ? dayjs(startDate) : null;
        const end = endDate ? dayjs(endDate) : null;
        return convertedQuotations
            .filter((q) => q.names.includes(name))
            .filter((q) => {
                if (!q.date || !q.date.isValid()) return true; // no date info -> include (fallback)
                if (start && q.date.isBefore(start, "day")) return false;
                if (end && q.date.isAfter(end, "day")) return false;
                return true;
            })
            .reduce((s, q) => s + q.amt, 0);
    };

    const achievedForRow = (r) => {
        if (r.sales_executive_name) {
            return achievedForNameInRange(r.sales_executive_name, r.start_date, r.end_date);
        }
        const mt = Array.isArray(r.member_targets) ? r.member_targets
            : (() => { try { return JSON.parse(r.member_targets || "[]"); } catch { return []; } })();
        return mt.reduce((s, m) => s + achievedForNameInRange(m.member_name, r.start_date, r.end_date), 0);
    };

    // Filter rows by the selected target type / period / month / territory / status
    const filtered = useMemo(() => {
        return rows.filter((r) => {
            const rowTargetFor = r.target_for === "Sales Team" ? "Sales Team" : "Sales Executive";
            if (rowTargetFor !== targetFor) return false;
            if (period !== "History" && r.period && r.period !== period) return false;
            if (period !== "History" && month && r.target_month) {
                const expected = period === "Yearly" ? month.format("YYYY")
                    : period === "Quarterly" ? `Q${month.quarter()} ${month.format("YYYY")}`
                        : month.format("MMMM YYYY");
                if (r.target_month !== expected) return false;
            }
            if (territory && r.territory !== territory) return false;
            const rev = Number(r.revenue_target || 0);
            const ach = Number(r.achieved_amount || 0);
            const pct = rev ? (ach / rev) * 100 : 0;
            if (status && statusFor(pct).label !== status) return false;
            return true;
        });
    }, [rows, targetFor, period, month, territory, status]);

    // Aggregate summary numbers
    const summary = useMemo(() => {
        const totalTarget = filtered.reduce((s, r) => s + Number(r.revenue_target || 0), 0);
        const totalAchieved = filtered.reduce((s, r) => s + Number(r.achieved_amount || 0), 0);
        const totalPending = Math.max(0, totalTarget - totalAchieved);
        const overall = totalTarget ? ((totalAchieved / totalTarget) * 100) : 0;

        let onTrack = 0, atRisk = 0, behind = 0;
        filtered.forEach((r) => {
            const rev = Number(r.revenue_target || 0);
            const ach = Number(r.achieved_amount || 0);
            const pct = rev ? (ach / rev) * 100 : 0;
            const s = statusFor(pct).label;
            if (s === "On Track") onTrack += 1;
            else if (s === "At Risk") atRisk += 1;
            else behind += 1;
        });

        return { totalTarget, totalAchieved, totalPending, overall, onTrack, atRisk, behind, count: filtered.length };
    }, [filtered]);

    // Fixed top stat cards — always reflect the real current calendar month, never affected by tab/period/month picker below.
    const currentMonthSummary = useMemo(() => {
        const now = dayjs();
        const currentMonthStr = now.format("MMMM YYYY");

        const thisMonthRows = rows.filter((r) => r.period === "Monthly" && r.target_month === currentMonthStr);

        const namesInView = new Set();
        thisMonthRows.forEach((r) => {
            if (r.sales_executive_name) namesInView.add(r.sales_executive_name);
            const mt = Array.isArray(r.member_targets) ? r.member_targets
                : (() => { try { return JSON.parse(r.member_targets || "[]"); } catch { return []; } })();
            mt.forEach((m) => { if (m.member_name) namesInView.add(m.member_name); });
        });

        const totalTarget = thisMonthRows.reduce((s, r) => s + Number(r.revenue_target || 0), 0);

        const totalAchieved = convertedQuotations
            .filter((q) => q.names.some((n) => namesInView.has(n)))
            .filter((q) => {
                if (!q.date || !q.date.isValid()) return true;
                return q.date.isSame(now, "month") && q.date.isSame(now, "year");
            })
            .reduce((s, q) => s + q.amt, 0);

        const totalPending = Math.max(0, totalTarget - totalAchieved);
        const overall = totalTarget ? ((totalAchieved / totalTarget) * 100) : 0;

        return { totalTarget, totalAchieved, totalPending, overall };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, convertedQuotations]);

    // Overdue Targets — any target row (any period, any month) whose end_date has passed without hitting the goal. Always global.
    const overdueCount = useMemo(() => {
        const today = dayjs().startOf("day");
        return rows.filter((r) => {
            if (!r.end_date) return false;
            const end = dayjs(r.end_date);
            const rev = Number(r.revenue_target || 0);
            const ach = achievedForRow(r);
            return end.isBefore(today) && ach < rev;
        }).length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, convertedQuotations]);

    // Achievement Overview donut + status counts — based on ALL rows, independent of tab/period filters
    const globalSummary = useMemo(() => {
        let onTrack = 0, atRisk = 0, behind = 0;
        rows.forEach((r) => {
            const rev = Number(r.revenue_target || 0);
            const ach = achievedForRow(r);
            const pct = rev ? (ach / rev) * 100 : 0;
            const s = statusFor(pct).label;
            if (s === "On Track") onTrack += 1;
            else if (s === "At Risk") atRisk += 1;
            else behind += 1;
        });
        const totalTarget = rows.reduce((s, r) => s + Number(r.revenue_target || 0), 0);
        return { totalTarget, onTrack, atRisk, behind, count: rows.length };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, convertedQuotations]);

    // "Target Summary (All)" card — true all-time totals, deduped, fixed regardless of tabs
    const globalAllTimeSummary = useMemo(() => {
        const totalTarget = rows.reduce((s, r) => s + Number(r.revenue_target || 0), 0);
        const namesInView = new Set();
        rows.forEach((r) => {
            if (r.sales_executive_name) namesInView.add(r.sales_executive_name);
            const mt = Array.isArray(r.member_targets) ? r.member_targets
                : (() => { try { return JSON.parse(r.member_targets || "[]"); } catch { return []; } })();
            mt.forEach((m) => { if (m.member_name) namesInView.add(m.member_name); });
        });
        const totalAchieved = convertedQuotations
            .filter((q) => q.names.some((n) => namesInView.has(n)))
            .reduce((s, q) => s + q.amt, 0);
        const totalPending = Math.max(0, totalTarget - totalAchieved);
        const overall = totalTarget ? ((totalAchieved / totalTarget) * 100) : 0;
        return { totalTarget, totalAchieved, totalPending, overall };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, convertedQuotations]);

    const topPerformers = useMemo(() => {
        const byName = {};
        rows.forEach((r) => {
            const name = r.sales_executive_name || r.team_name || "—";
            if (!byName[name]) byName[name] = { name, target: 0, achieved: 0 };
            byName[name].target += Number(r.revenue_target || 0);
            byName[name].achieved += achievedForRow(r);
        });
        return Object.values(byName)
            .map((e) => ({ name: e.name, pct: e.target ? (e.achieved / e.target) * 100 : 0 }))
            .sort((a, b) => b.pct - a.pct)
            .slice(0, 3);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, convertedQuotations]);

    const columns = [
        { title: "#", key: "idx", width: 50, render: (_, __, i) => i + 1 },
        {
            title: targetFor === "Sales Team" ? "Sales Team" : "Sales Executive",
            dataIndex: "sales_executive_name", key: "name",
            render: (v, r) => v || r.team_name || "—",
        },
        ...(targetFor === "Sales Team" ? [] : [
            { title: "Designation", dataIndex: "designation", key: "designation", render: (v) => v || "—" },
        ]),
        ...(period === "History" ? [
            {
                title: "Period", key: "period_label", width: 90,
                render: (_, r) => <Tag>{r.period || "—"}</Tag>,
            },
            {
                title: "Month / Quarter / Year", dataIndex: "target_month", key: "target_month",
                render: (v) => {
                    if (!v) return "—";
                    const qMatch = v.match(/^Q(\d)\s+(\d{4})$/);
                    if (qMatch) {
                        const qLabels = { 1: "Jan - Mar", 2: "Apr - Jun", 3: "Jul - Sep", 4: "Oct - Dec" };
                        return `${qLabels[qMatch[1]]} ${qMatch[2]}`;
                    }
                    return v;
                },
            },
        ] : []),
        { title: "Territory", dataIndex: "territory", key: "territory", render: (v) => v || "—" },
        {
            title: "Revenue Target (₹)", dataIndex: "revenue_target", key: "revenue_target",
            align: "right", render: (v) => Number(v || 0).toLocaleString("en-IN"),
        },
        {
            title: "Achieved (₹)", key: "achieved_amount",
            align: "right", render: (_, r) => achievedForRow(r).toLocaleString("en-IN"),
        },
        {
            title: "Ach. (%)", key: "ach_pct", align: "right",
            render: (_, r) => {
                const rev = Number(r.revenue_target || 0);
                const ach = achievedForRow(r);
                const pct = rev ? (ach / rev) * 100 : 0;
                return `${Math.min(pct, 100).toFixed(2)}%`;
            },
        },
        {
            title: "Status", key: "status", align: "center",
            render: (_, r) => {
                const rev = Number(r.revenue_target || 0);
                const ach = achievedForRow(r);
                const pct = rev ? (ach / rev) * 100 : 0;
                const s = statusFor(pct);
                return <Tag color={s.color}>{s.label}</Tag>;
            },
        },
        {
            title: "Action", key: "action", align: "center", width: 130,
            render: (_, r) => (
                <Space size="small">
                    <Tooltip title="View">
                        <Button size="small" type="text" icon={<EyeOutlined />}
                            onClick={() => navigate(`/add-sales-target?id=${r.id}&mode=view`)} />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "No permission to edit" : "Edit"}>
                        <Button size="small" type="text" icon={<EditOutlined />} disabled={!perm.edit}
                            onClick={() => navigate(`/add-sales-target?id=${r.id}&mode=edit`)} />
                    </Tooltip>
                    <Popconfirm title="Delete this target?" okText="Yes" cancelText="No"
                        okButtonProps={{ danger: true }} disabled={!perm.delete}
                        onConfirm={() => handleDelete(r.id)}>
                        <Tooltip title={!perm.delete ? "No permission to delete" : "Delete"}>
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={!perm.delete} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (!perm.read) {
        return (
            <Card style={{ margin: 16 }}>
                <Typography.Text type="danger">You don't have permission to view this page.</Typography.Text>
            </Card>
        );
    }

    const targetForTabs = [
        { key: "Sales Executive", label: "Sales Executive" },
        { key: "Sales Team", label: "Sales Team" },
    ];

    const periodTabs = [
        { key: "Monthly", label: "Monthly Targets" },
        { key: "Quarterly", label: "Quarterly Targets" },
        { key: "Yearly", label: "Yearly Targets" },
        { key: "History", label: "Target History" },
    ];

    const donutTotal = globalSummary.onTrack + globalSummary.atRisk + globalSummary.behind || 1;
    let acc = 0;
    const stops = [
        { c: "#52c41a", v: globalSummary.onTrack },
        { c: "#fa8c16", v: globalSummary.atRisk },
        { c: "#ff4d4f", v: globalSummary.behind },
    ].map((s) => {
        const start = (acc / donutTotal) * 360;
        acc += s.v;
        const end = (acc / donutTotal) * 360;
        return `${s.c} ${start}deg ${end}deg`;
    }).join(", ");

    return (
        <div>
            {msgCtx}
            <Card
                style={{ marginBottom: 12 }}
                title={
                    <div>
                        <Typography.Title level={4} style={{ margin: 0 }}>Sales Targets</Typography.Title>

                    </div>
                }
                extra={
                    <Space>
                        <Button icon={<HistoryOutlined />} onClick={() => setPeriod("History")}>Target History</Button>
                        <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                            <Button type="primary" icon={<PlusOutlined />} disabled={!perm.add}
                                style={{ background: PRIMARY, borderColor: PRIMARY }}
                                onClick={() => navigate("/add-sales-target")}>
                                Add Target
                            </Button>
                        </Tooltip>
                    </Space>
                }
            >
                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<TeamOutlined />} color="#1677ff" label="Total Sales Executives"
                            value={totalEmployees}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<AimOutlined />} color="#52c41a" label="Total Target (This Month)"
                            value={inr(currentMonthSummary.totalTarget)} sub="" />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<CheckCircleOutlined />} color="#722ed1" label="Achieved (This Month)"
                            value={inr(currentMonthSummary.totalAchieved)}
                            sub={`${currentMonthSummary.overall.toFixed(2)}% of Target`} subColor="#52c41a" />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<ClockCircleOutlined />} color="#fa8c16" label="Pending (This Month)"
                            value={inr(currentMonthSummary.totalPending)}
                            sub={`${Math.max(0, 100 - currentMonthSummary.overall).toFixed(2)}% of Target`} subColor="#fa8c16" />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                        <StatCard icon={<WarningOutlined />} color="#ff4d4f" label="Overdue Targets"
                            value={overdueCount} />
                    </Col>
                </Row>
                <Row gutter={12} style={{ marginTop: 12 }}>
                    {/* LEFT: tabs + filters + table */}
                    <   Col xs={24} lg={16}>
                        <Card size="small" styles={{ body: { paddingTop: 8 } }}>
                            <Tabs activeKey={targetFor} onChange={setTargetFor} items={targetForTabs} />
                            <Tabs activeKey={period} onChange={setPeriod} items={periodTabs} size="small" />

                            <Row gutter={8} style={{ marginBottom: 12 }}>
                                <Col flex="160px">
                                    <DatePicker
                                        picker={period === "Yearly" ? "year" : period === "Quarterly" ? "quarter" : "month"}
                                        style={{ width: "100%" }}
                                        format={period === "Yearly" ? "YYYY" : period === "Quarterly" ? "[Q]Q YYYY" : "MMMM YYYY"}
                                        value={month} onChange={setMonth} allowClear={false}
                                        disabled={period === "History"}
                                    />
                                </Col>
                                <Col flex="160px">
                                    <Select showSearch allowClear style={{ width: "100%" }}
                                        placeholder="All Territories" options={territories}
                                        value={territory} onChange={setTerritory}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col flex="160px">
                                    <Select allowClear style={{ width: "100%" }}
                                        placeholder="All Status"
                                        value={status} onChange={setStatus}
                                        options={[
                                            { value: "On Track", label: "On Track" },
                                            { value: "At Risk", label: "At Risk" },
                                            { value: "Behind", label: "Behind" },
                                        ]} />
                                </Col>
                                <Col>
                                    <Button icon={<FilterOutlined />} onClick={() => {
                                        setTerritory(null); setStatus(null);
                                    }}>Clear</Button>
                                </Col>
                            </Row>

                            <Table
                                rowKey="id"
                                size="small"
                                loading={loading}
                                columns={columns}
                                dataSource={filtered}
                                pagination={{ pageSize: 10, size: "small" }}
                                locale={{ emptyText: <Empty description="No targets found" /> }}
                                expandable={targetFor === "Sales Team" ? {
                                    rowExpandable: (r) => r.target_for === "Sales Team",
                                    expandedRowRender: (r) => {
                                        const mt = Array.isArray(r.member_targets) ? r.member_targets
                                            : (() => { try { return JSON.parse(r.member_targets || "[]"); } catch { return []; } })();
                                        const leaderId = r.team_leader_id ? String(r.team_leader_id) : null;
                                        const memberColumns = [
                                            { title: "#", key: "midx", width: 50, render: (_, __, i) => i + 1 },
                                            {
                                                title: "Member Name", key: "member_name",
                                                render: (m) => {
                                                    const isLeader = leaderId && String(m.member_id) === leaderId;
                                                    return (
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                            {m.member_name || m.member_id || "—"}
                                                            {isLeader && (
                                                                <Tooltip title="Team Leader">
                                                                    <span style={{ color: "#fa8c16" }}>★</span>
                                                                </Tooltip>
                                                            )}
                                                        </span>
                                                    );
                                                },
                                            },
                                            {
                                                title: "Revenue Target (₹)", dataIndex: "revenue_target", key: "m_revenue_target",
                                                align: "right", render: (v) => Number(v || 0).toLocaleString("en-IN"),
                                            },
                                            {
                                                title: "Achieved (₹)", key: "m_achieved",
                                                align: "right", render: (m) => achievedForNameInRange(m.member_name, r.start_date, r.end_date).toLocaleString("en-IN"),
                                            },
                                            {
                                                title: "Ach. (%)", key: "m_ach_pct", align: "right",
                                                render: (m) => {
                                                    const rev = Number(m.revenue_target || 0);
                                                    const ach = achievedForNameInRange(m.member_name, r.start_date, r.end_date);
                                                    const pct = rev ? (ach / rev) * 100 : 0;
                                                    return `${Math.min(pct, 100).toFixed(2)}%`;
                                                },
                                            },
                                            {
                                                title: "Status", key: "m_status", align: "center",
                                                render: (m) => {
                                                    const rev = Number(m.revenue_target || 0);
                                                    const ach = achievedForNameInRange(m.member_name, r.start_date, r.end_date);
                                                    const pct = rev ? (ach / rev) * 100 : 0;
                                                    const s = statusFor(pct);
                                                    return <Tag color={s.color}>{s.label}</Tag>;
                                                },
                                            },
                                        ];
                                        return (
                                            <div style={{ padding: "8px 0" }}>
                                                <Typography.Text strong style={{ fontSize: 12, color: "#555", marginBottom: 8, display: "block" }}>
                                                    Team Members
                                                </Typography.Text>
                                                <Table
                                                    size="small"
                                                    rowKey={(m) => m.member_id}
                                                    columns={memberColumns}
                                                    dataSource={mt}
                                                    pagination={false}
                                                    locale={{ emptyText: "No members found" }}
                                                />
                                            </div>
                                        );
                                    },
                                } : undefined}
                            />
                        </Card>
                    </Col>

                    {/* RIGHT: summary + donut + top performers */}
                    <Col xs={24} lg={8}>
                        <Card size="small" style={{ marginBottom: 12 }}
                            title={<Typography.Text strong>Target Summary (All)</Typography.Text>}>
                            {[
                                { label: "Total Revenue Target", val: inr(globalAllTimeSummary.totalTarget) },
                                { label: "Total Achieved", val: inr(globalAllTimeSummary.totalAchieved) },
                                { label: "Total Pending", val: inr(globalAllTimeSummary.totalPending) },
                            ].map((r, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f5f5f5" }}>
                                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>{r.label}</Typography.Text>
                                    <Typography.Text strong style={{ fontSize: 13 }}>{r.val}</Typography.Text>
                                </div>
                            ))}
                            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8 }}>
                                <Typography.Text strong style={{ color: "#52c41a" }}>Overall Achievement</Typography.Text>
                                <Typography.Text strong style={{ color: "#52c41a" }}>{globalAllTimeSummary.overall.toFixed(2)}%</Typography.Text>
                            </div>
                            <Progress percent={Number(globalAllTimeSummary.overall.toFixed(0))} showInfo={false} strokeColor="#52c41a" />
                        </Card>

                        <Card size="small" style={{ marginBottom: 12 }}
                            title={<Typography.Text strong>Achievement Overview</Typography.Text>}>
                            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                                <div style={{ position: "relative", width: 130, height: 130, flexShrink: 0 }}>
                                    <div style={{
                                        width: 130, height: 130, borderRadius: "50%",
                                        background: `conic-gradient(${stops})`,
                                    }} />
                                    <div style={{
                                        position: "absolute", top: 20, left: 20, width: 90, height: 90,
                                        borderRadius: "50%", background: "#fff",
                                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <div style={{ fontSize: 22, fontWeight: 700 }}>{totalEmployees}</div>
                                        <div style={{ fontSize: 11, color: "#999" }}>Executives</div>
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    {[
                                        { label: "On Track (≥70%)", val: globalSummary.onTrack, color: "#52c41a" },
                                        { label: "At Risk (40–69%)", val: globalSummary.atRisk, color: "#fa8c16" },
                                        { label: "Behind (<40%)", val: globalSummary.behind, color: "#ff4d4f" },
                                    ].map((s, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                            <Space size={6}>
                                                <span style={{ width: 10, height: 10, borderRadius: "50%", background: s.color, display: "inline-block" }} />
                                                <Typography.Text style={{ fontSize: 12 }}>{s.label}</Typography.Text>
                                            </Space>
                                            <Typography.Text strong style={{ fontSize: 13 }}>{s.val}</Typography.Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>

                        <Card size="small"
                            title={<Typography.Text strong>Top Performers</Typography.Text>}
                            extra={<Typography.Link onClick={() => setStatus(null)}>View All</Typography.Link>}>
                            {topPerformers.length ? topPerformers.map((p, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                    <Typography.Text type="secondary" style={{ width: 14 }}>{i + 1}</Typography.Text>
                                    <Typography.Text style={{ flex: 1, fontSize: 13 }}>{p.name}</Typography.Text>
                                    <div style={{ width: 90 }}>
                                        <Progress percent={Number(p.pct.toFixed(0))} size="small" strokeColor="#52c41a" />
                                    </div>
                                    <Typography.Text strong style={{ fontSize: 12, width: 56, textAlign: "right" }}>
                                        {Math.min(p.pct, 100).toFixed(2)}%
                                    </Typography.Text>
                                </div>
                            )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />}
                        </Card>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default SalesTargets;